import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import classes from '@nerdwallet/base-styles/classes';
import { withRouter } from 'react-router';

import { CenteredContainer, Title, Text, Box } from '@nerdwallet/currency';

import Hero from '../employee-branding/hero/hero';
import PressLogoSalad from '../press-page/press-logo-salad/press-logo-salad';
import ZigZag from '../employee-branding/zig-zag/zig-zag';
import NerdCarousel from '../employee-branding/nerd-carousel/nerd-carousel';
import CompanyFooter from '../employee-branding/company-footer/company-footer';
import SocialMedia from './social-media/social-media';

import NerdCarouselTilesMobile from '~/client/lib/employee-branding/nerd-carousel-tiles-mobile';
import NerdCarouselTilesDesktop from '~/client/lib/employee-branding/nerd-carousel-tiles-desktop';
import AwardsLogoTiles from '~/client/lib/employee-branding/awards';

import grnhseTrackingCheck from '~/client/lib/company-content-src/grnhse-tracking-check';

import {
  CAREERS_TEAMS_PATH,
  DIVERSITY_INCLUSION_PATH,
  BENEFITS_PATH,
} from '~/client/lib/employee-branding/careers-breadcrumbs-paths';

import styles from './careers-info-page.module.less';

const REMOTE_COPY = [
  {
    header: '',
    copy: (
      <Fragment>
        While we’ve got offices in San Francisco, New York, and the UK, we’ve
        transitioned to a remote-first workplace with most of our jobs offered
        as fully remote from anywhere in the U.S.
      </Fragment>
    ),
  },
];

const PERKS_COPY = [
  {
    header: '',
    copy: (
      <Fragment>
        We value our Nerds and know that happy, fulfilled people do their best
        work. With competitive health insurance for you and your whole family,
        mental health support, retirement plans, a commitment to employee growth
        and empowerment, plus a great work-life balance, our perks and benefits
        help our Nerds remain exceptional.
      </Fragment>
    ),
  },
];

const DIVERSITY_COPY = [
  {
    header: '',
    copy: (
      <Fragment>
        At NerdWallet, we aspire to provide people with the confidence they need
        to live their best lives—however they identify. This means fostering an
        inclusive culture that allows all Nerds to grow their skills,
        contribute, and thrive with the confidence of belonging.
        <br />
        <br /> Visit our DEI page to learn about our current and ongoing
        efforts.
      </Fragment>
    ),
  },
];

const CareersInfoPage = ({ location: routerLocation }) => (
  <div>
    <div
      className={classNames([
        classes('margin-bottom-4'),
        classes('margin-bottom-5--wide'),
      ])}
    >
      <Hero
        backgroundColor="#DDFDF2"
        title="Join our Nerds"
        copy="If you’re ready to help bring clarity to the finance world while doing the best work of your career, we’re ready for you."
        imageUrlMobile="https://www.nerdwallet.com/cdn/company/2023/hero/Company-Pages-Header-Careers-Mobile.jpg"
        imageUrlDesktop="https://www.nerdwallet.com/cdn/company/2023/hero/Company-Pages-Header-Careers-Desktop.jpg"
        ctaUrl={grnhseTrackingCheck(CAREERS_TEAMS_PATH, routerLocation.search)}
        ctaText="see open positions"
      />
    </div>
    <CenteredContainer>
      <Title
        size="small"
        className={classNames([
          classes('margin-horizontal-auto'),
          classes('margin-bottom-3'),
          classes('margin-bottom-4--wide'),
          classes('text-align-center'),
        ])}
        component="h2"
      >
        Bring your best every day
      </Title>
      <div
        className={classNames([
          classes('margin-horizontal-auto'),
          classes('margin-bottom-3-half'),
          classes('margin-bottom-5--wide'),
          classes('text-align-center'),
          classes('width-two-thirds--wide'),
        ])}
      >
        <Text>
          When you&apos;re at your best, it elevates those around you. We
          prioritize speaking candidly, treating others respectfully, and humbly
          embracing what we don&apos;t know. Our inclusive environment ensures
          every Nerd feels safe to be themselves — and to do their best work.
        </Text>
      </div>
    </CenteredContainer>
    <ZigZag
      backgroundColor="#008255"
      color="white"
      copyArray={REMOTE_COPY}
      imageUrlDesktop="https://www.nerdwallet.com/cdn/company/2021/zigzag/desktop/Remote-Illustration-Desktop.png"
      imageUrlMobile="https://www.nerdwallet.com/cdn/company/2021/zigzag/mobile/Remote-Illustration-Mobile.png"
      imageAlt=""
      title="Working remotely works for NerdWallet"
      positionImageBottom
    />
    <CenteredContainer>
      <Title
        size="large"
        chronicle
        className={classNames([
          classes('margin-horizontal-auto'),
          classes('margin-vertical-4'),
          classes('margin-vertical-5--wide'),
          classes('text-align-center'),
        ])}
        component="h2"
      >
        Proud to be recognized as a great place to work
      </Title>
      <div className={styles.pressLogoMobile}>
        <PressLogoSalad
          tiles={AwardsLogoTiles.mobileTiles}
          backgroundColor="#ffffff"
          verticalPadding="0px"
          isAwardLogo
        />
      </div>
      <div className={styles.pressLogoDesktop}>
        <PressLogoSalad
          tiles={AwardsLogoTiles.topTiles}
          backgroundColor="#ffffff"
          verticalPadding="0px"
          isAwardLogo
        />
        <PressLogoSalad
          tiles={AwardsLogoTiles.bottomTiles}
          backgroundColor="#ffffff"
          isAwardLogo
          verticalPadding="15px"
          topMargin={false}
        />
      </div>
      <div
        className={classNames([
          classes('margin-horizontal-4'),
          classes('margin-top-0'),
          classes('margin-top-2--wide'),
          classes('margin-bottom-5'),
          classes('text-align-center'),
        ])}
      >
        <Text size="small" light>
          Fortune Awards provided by FORTUNE. ©2022, ©2023 FORTUNE Media IP
          Limited. All rights reserved. Used under license.
        </Text>
        <br />
        <Text size="small" light>
          NerdWallet received the Real Simple Money Award 2023 for Life
          Insurance Comparison
        </Text>
      </div>
    </CenteredContainer>
    <ZigZag
      backgroundColor="#EEF7FF"
      color="neutral-darkest"
      copyArray={PERKS_COPY}
      imageUrlDesktop="https://www.nerdwallet.com/cdn/company/2021/zigzag/desktop/Careers-Benefits-teaser-Image-Desktop@2x.png"
      imageUrlMobile="https://www.nerdwallet.com/cdn/company/2021/zigzag/mobile/Careers-Benefits-teaser-Image-Mobile@2x.png"
      imageAlt=""
      title="Nerdy perks and benefits"
      ctaText="explore our benefits"
      ctaUrl={grnhseTrackingCheck(BENEFITS_PATH, routerLocation.search)}
      ctaPrimary={false}
      imageFirst
    />
    <CenteredContainer>
      <ZigZag
        color="neutral-darkest"
        copyArray={DIVERSITY_COPY}
        imageUrlDesktop="https://www.nerdwallet.com/cdn/diversity-inclusion/careers-dei.png"
        imageUrlMobile="https://www.nerdwallet.com/cdn/diversity-inclusion/careers-dei.png"
        imageAlt=""
        title="Diversity, Equity, and Inclusion Programs at NerdWallet"
        ctaText="LEARN MORE"
        ctaUrl={grnhseTrackingCheck(
          DIVERSITY_INCLUSION_PATH,
          routerLocation.search
        )}
        ctaPrimary={false}
      />
    </CenteredContainer>
    <Box backgroundColor="green-lightest" paddingY="4">
      <CenteredContainer>
        <Title
          size="small"
          className={classNames([
            classes('margin-horizontal-auto'),
            classes('margin-vertical-4'),
            classes('margin-vertical-5--wide'),
            classes('text-align-center'),
          ])}
          component="h2"
        >
          What our Nerds are saying
        </Title>
      </CenteredContainer>
      <div
        className={classNames([
          classes('display-block'),
          classes('display-none--wide'),
          classes('margin-bottom-3'),
        ])}
      >
        <NerdCarousel {...NerdCarouselTilesMobile} />
      </div>
      <div
        className={classNames([
          classes('display-none'),
          classes('display-block--wide'),
          classes('margin-bottom-5--wide'),
        ])}
      >
        <NerdCarousel {...NerdCarouselTilesDesktop} />
      </div>
    </Box>
    <SocialMedia />
    <CompanyFooter
      backgroundColor="#EEF7FF"
      contrastColor="#9DCDFA"
      title="Come nerd out with us"
      ctaText="see open positions"
      ctaUrl={grnhseTrackingCheck(CAREERS_TEAMS_PATH, routerLocation.search)}
      imageUrlMobile="https://www.nerdwallet.com/cdn/company/2021/footer/mobile/Careers-CTA-Illustration-Mobile_375.png"
      imageUrlDesktop="https://www.nerdwallet.com/cdn/company/2021/footer/desktop/Careers-CTA-Illustration-Desktop.png"
      isSmall={false}
    />
  </div>
);

CareersInfoPage.propTypes = {
  location: PropTypes.object,
};

CareersInfoPage.displayName = 'CareersInfoPage';

const CareersInfoPageWithRouter = withRouter(CareersInfoPage);

export default CareersInfoPageWithRouter;
